import CustomerJson, { validateRentalBookingCustomerJson, validateRentalBookingCustomerJsonRequest } from "./CustomerJson";
import RentalItemJson, { validateRentalItemJsonBooking } from "./RentalItemJson";
import RentalConditionJson, { defaultRentalConditionJson, validateRentalConditionJson, getRentalConditionPrice } from "./RentalConditionJson";
import ClientJson from "./ClientJson";
import RentalAccountJson from "./RentalAccountJson";
import { RentalBookingStatus } from "./RentalBookingStatus";
import SepaMandateJson, { validateSepaMandate, validateSepaMandateAddNew } from "./SepaMandateJson";
import InvoiceInfoJson from "./InvoiceInfoJson";
import { Validation, ValidationResult, ValidationErrorType } from "./Validation";
import PriceUtils from "./PriceUtils";
import RentalTimeSlotJson, { validateRentalTimeSlotJson } from "./RentalTimeSlotJson"

/**
 * Buchung eines Mietgegenstands. Warenkörbe werden nur mehrere einzelne RentalBookings realisiert.
 */
export default interface RentalBookingJson {
    ID: number | null;

    /**
     * Mietgegenstand dieser Buchung. Wird in der Datenbank als Referenz abgespeichert.
     * Kann null sein, wenn der Mietgegenstand gelöscht wurde.
     */
    rentalItem: RentalItemJson | null;

    /**
     * Verliehene Anzahl des Gegenstands.
     */
    amount: number;

    /**
     * Startdatum des Verleihs.
     */
    beginDate: string;

    /**
     * Enddatum des Verleihs.
     */
    endDate: string;

    /**
     * Mietbedingungen dieser Buchung. Wird in der Datenbank als Kopie abgespeichert, daher können hier Änderungen für einzelne Buchungen separat vorgenommen werden.
     */
    rentalCondition: RentalConditionJson;

    /**
     * Gebuchte Extras der Buchung. Manuelle Einträge für Reinigungsgebühren etc. sind möglich.
     */
    additionalRentalConditions: RentalConditionJson[];

    /**
     * Kunde der Buchung.
     */
    customer: CustomerJson | null;

    /**
     * Client/Account dieser Buchung.
     */
    client: ClientJson | null;

    /**
     * Buchungsaccount dieser Buchung.
     */
    rentalAccount: RentalAccountJson | null;

    /**
     * Freitextfeld für die Verwaltung.
     */
    remarks: string;

    /**
     * Gibt an, ob eine Außnahme von der Mehrwersteuerpflicht besteht (Jugendarbeit).
     */
    isVATExempt: boolean | null;

    /**
     * Begründungsfeld für Außnahme von der Mehrwersteuerpflicht (Jugendarbeit).
     */
    remarksVATExemption: string;

    /**
     * Gibt den Zeitraum der Abholung an. Wird nur abgefragt wenn Settings rentalRequestPickupAndReturnTime true.
     */
    pickupTime: RentalTimeSlotJson | null;

    /**
     * Gibt den Zeitraum der Rückgabe an. Wird nur abgefragt wenn Settings rentalRequestPickupAndReturnTime true.
     */
    returnTime: RentalTimeSlotJson | null;

    /**
    * Datum und Uhrzeit zu der gebucht wurde.
    */
    bookingDateTime: string;

    /**
    * Datum und Uhrzeit zu der storniert wurde.
    */
    cancellationDateTime: string | null;

    /**
     * Buchungsstatus.
     */
    state: RentalBookingStatus;

    /**
     * SEPA-Lastschriftmandat für die Zahlung. Kann auch null sein, wenn eine Bar oder Rechnungszahlung gewünscht ist.
     */
    sepaMandate: SepaMandateJson | null;

    /**
     * Rechnungsinformationen wie Rechnungsnummer.
     */
    invoiceInfos: InvoiceInfoJson[] | null;
}
export type RentalBookingCustomerViewJson = Pick<RentalBookingJson, "ID" | "rentalItem" | "beginDate" | "endDate" | "amount" | "rentalCondition" | "additionalRentalConditions" | "customer" | "state" | "isVATExempt" | "remarksVATExemption" | "pickupTime" | "returnTime"> & { "canStillBeModified": boolean; };

export function defaultRentalBookingJson(): RentalBookingJson {
    return {
        ID: null,
        rentalItem: null,
        amount: 1,
        beginDate: "",
        endDate: "",
        rentalCondition: defaultRentalConditionJson(),
        additionalRentalConditions: [],
        customer: null,
        client: null,
        rentalAccount: null,
        remarks: "",
        isVATExempt: false,
        remarksVATExemption: "",
        pickupTime: null,
        returnTime: null,
        bookingDateTime: new Date().toISOString(),
        cancellationDateTime: null,
        state: RentalBookingStatus.requested,
        sepaMandate: null,
        invoiceInfos: null
    };
}

export function getRentalBookingTotalPrice(rentalBooking: RentalBookingJson | RentalBookingCustomerViewJson): { priceIncludingVAT: number, priceExcludingVAT: number } {
    var totalPrice = {
        priceIncludingVAT: 0,
        priceExcludingVAT: 0
    };

    if (rentalBooking != null && rentalBooking.amount && rentalBooking.beginDate && rentalBooking.endDate) {
        let rentalConditionPrice = getRentalConditionPrice(
            rentalBooking.rentalCondition,
            rentalBooking.amount,
            new Date(rentalBooking.beginDate),
            new Date(rentalBooking.endDate)
        );

        totalPrice.priceIncludingVAT = rentalConditionPrice
        totalPrice.priceExcludingVAT = PriceUtils.getPriceExcludingVAT(rentalConditionPrice, rentalBooking.rentalCondition.priceIncludedVATRate);

        for (let c of rentalBooking.additionalRentalConditions) {
            let rentalAdditionalConditionPrice = getRentalConditionPrice(
                c,
                rentalBooking.amount,
                new Date(rentalBooking.beginDate),
                new Date(rentalBooking.endDate)
            );

            //let rentalAdditionalConditionPrice = getRentalAdditionalConditionPrice(c);

            totalPrice.priceIncludingVAT += rentalAdditionalConditionPrice;
            totalPrice.priceExcludingVAT += PriceUtils.getPriceExcludingVAT(rentalAdditionalConditionPrice, c.priceIncludedVATRate);
        }
    }

    return totalPrice;
}

export function validateRentalBookingJson(json: RentalBookingJson): ValidationResult<RentalBookingJson> {
    return new Validation(json)
        .validateInteger("ID", 0, null, false)
        .validateInteger("amount", 1, null, false)
        .validateUsingFunction("rentalItem", validateRentalItemJsonBooking)
        .validateDate("beginDate")
        .validateDateLater("endDate", "beginDate")
        .validateUsingFunction("rentalCondition", validateRentalConditionJson)
        .validateAllUsingFunction("additionalRentalConditions", createValidateRentalConditionJsonTitleUnique(json.additionalRentalConditions.map(c => c.title)), 0)
        .validateString("remarks", 1000)
        .validateBoolean("isVATExempt")
        .validateUsingFunction("pickupTime", validateRentalTimeSlotJson, true)
        .validateUsingFunction("returnTime", validateRentalTimeSlotJson, true)
        .validateDate("bookingDateTime")
        .validateDate("cancellationDateTime", !isRentalBookingStateCancellation(json.state))
        .validateUsingFunction("customer", validateRentalBookingCustomerJson, json.state == RentalBookingStatus.internalReservation)
        .validateUsingFunction("sepaMandate", validateSepaMandate, true)
        .validateString("remarksVATExemption", 1000)
        .finish();
}

export function validateRentalBookingJsonAdd(json: RentalBookingJson): ValidationResult<RentalBookingJson> {
    return new Validation(json)
        .validateEquals("ID", null)
        .validateInteger("amount", 1, null, false)
        .validateUsingFunction("rentalItem", validateRentalItemJsonBooking)
        .validateDate("beginDate")
        .validateDateLater("endDate", "beginDate")
        .validateUsingFunction("rentalCondition", validateRentalConditionJson)
        .validateAllUsingFunction("additionalRentalConditions", createValidateRentalConditionJsonTitleUnique(json.additionalRentalConditions.map(c => c.title)))
        .validateString("remarks", 1000)
        .validateBoolean("isVATExempt")
        .validateUsingFunction("pickupTime", validateRentalTimeSlotJson, true)
        .validateUsingFunction("returnTime", validateRentalTimeSlotJson, true)
        .validateDate("bookingDateTime")
        .validateDate("cancellationDateTime", !isRentalBookingStateCancellation(json.state))
        .validateUsingFunction("customer", validateRentalBookingCustomerJsonRequest, json.state == RentalBookingStatus.internalReservation)
        .validateUsingFunction("sepaMandate", validateSepaMandateAddNew, true)
        .validateString("remarksVATExemption", 1000)
        .finish();
}

export function validateRentalBookingJsonRequest(
    json: RentalBookingJson, 
    rentalRequestPickupAndReturnTime: boolean = false,
    requestDriversLicense: boolean = false
): ValidationResult<RentalBookingJson> {
    let validation = new Validation(json)
        .validateEquals("ID", null)
        .validateInteger("amount", 1, null, false)
        .validateUsingFunction("rentalItem", validateRentalItemJsonBooking)
        .validateDate("beginDate")
        .validateDateLater("endDate", "beginDate")
        .validateUsingFunction("rentalCondition", validateRentalConditionJson)
        .validateAllUsingFunction("additionalRentalConditions", validateRentalConditionJson, 0)
        .validateString("remarks", 1000)
        .validateBoolean("isVATExempt")
        .validateUsingFunction("pickupTime", validateRentalTimeSlotJson, !rentalRequestPickupAndReturnTime)
        .validateUsingFunction("returnTime", validateRentalTimeSlotJson, !rentalRequestPickupAndReturnTime)
        .validateDate("bookingDateTime")
        .validateDate("cancellationDateTime", !isRentalBookingStateCancellation(json.state))
        .validateEquals("client", null)
        .validateEquals("rentalAccount", null)
        .validateUsingFunction("customer", (v) => validateRentalBookingCustomerJsonRequest(v, requestDriversLicense))
        .validateUsingFunction("sepaMandate", validateSepaMandateAddNew, true)
        .validateEquals("state", RentalBookingStatus.requested);

    if (json.isVATExempt) {
        validation = validation.validateNonEmptyString("remarksVATExemption", 1000);
    } else {
        validation = validation.validateString("remarksVATExemption", 1000);
    }
    return validation.finish();
}

export function validateRentalBookingJsonAccountRequest(
    json: RentalBookingJson, 
    rentalRequestPickupAndReturnTime: boolean = false, 
    requestDriversLicense: boolean = false
): ValidationResult<RentalBookingJson> {
    let validation = new Validation(json)
        .validateEquals("ID", null)
        .validateInteger("amount", 1, null, false)
        .validateUsingFunction("rentalItem", validateRentalItemJsonBooking)
        .validateDate("beginDate")
        .validateDateLater("endDate", "beginDate")
        .validateUsingFunction("rentalCondition", validateRentalConditionJson)
        .validateAllUsingFunction("additionalRentalConditions", validateRentalConditionJson, 0)
        .validateString("remarks", 1000)
        .validateBoolean("isVATExempt")
        .validateUsingFunction("pickupTime", validateRentalTimeSlotJson, !rentalRequestPickupAndReturnTime)
        .validateUsingFunction("returnTime", validateRentalTimeSlotJson, !rentalRequestPickupAndReturnTime)
        .validateDate("bookingDateTime")
        .validateDate("cancellationDateTime", !isRentalBookingStateCancellation(json.state))
        .validateEquals("client", null)
        .validateEquals("rentalAccount", null)
        .validateUsingFunction("customer", (v) => validateRentalBookingCustomerJsonRequest(v, requestDriversLicense))
        .validateUsingFunction("sepaMandate", validateSepaMandateAddNew, true)
        .validateEquals("state", RentalBookingStatus.requested)
        .validateString("remarksVATExemption", 1000);

    if (json.isVATExempt) {
        validation = validation.validateNonEmptyString("remarksVATExemption", 1000);
    } else {
        validation = validation.validateString("remarksVATExemption", 1000);
    }
    return validation.finish();
}

export function validateRentalBookingJsonCartItem(json: RentalBookingJson, rentalRequestPickupAndReturnTime: boolean = false): ValidationResult<RentalBookingJson> {
    return new Validation(json)
        .validateEquals("ID", null)
        .validateInteger("amount", 1, null, false)
        .validateUsingFunction("rentalItem", validateRentalItemJsonBooking)
        .validateDate("beginDate")
        .validateDateLater("endDate", "beginDate")
        .validateUsingFunction("rentalCondition", validateRentalConditionJson)
        .validateAllUsingFunction("additionalRentalConditions", validateRentalConditionJson, 0)
        .validateString("remarks", 1000)
        .validateBoolean("isVATExempt")
        .validateUsingFunction("pickupTime", validateRentalTimeSlotJson, !rentalRequestPickupAndReturnTime)
        .validateUsingFunction("returnTime", validateRentalTimeSlotJson, !rentalRequestPickupAndReturnTime)
        .validateEquals("client", null)
        .validateEquals("rentalAccount", null)
        .validateEquals("state", RentalBookingStatus.requested)
        .validateString("remarksVATExemption", 1000)
        .finish();
}

function isRentalBookingStateCancellation(state: RentalBookingStatus): boolean {
    if(
        state == RentalBookingStatus.cancellationPending
        || state == RentalBookingStatus.canceled
        || state == RentalBookingStatus.cancellationFeeExported
        || state == RentalBookingStatus.cancellationFeeReceived
    ) {
        return true;
    } else {
        return false;
    }
}

function createValidateRentalConditionJsonTitleUnique(titles: string[]): (c: RentalConditionJson) => ValidationResult<RentalConditionJson> {
    return function (c: RentalConditionJson) {
        let validation = validateRentalConditionJson(c);
        if (!validation.errors["title"] && titles.filter(t => t == c.title).length > 1) {
            validation.errors["title"] = ValidationErrorType.duplicate;
            return new ValidationResult(validation.errors);
        }
        return validation;
    };
}